import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormComponentOptions,
    FormValidator,
} from './form-component-options.model';
import { FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { FormType } from './form-type-enum';
import { Subscription } from 'rxjs';

@Component({
    selector: 'shared-app-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class FormComponent implements OnInit, OnDestroy {
    @Input() formOptions: FormComponentOptions[] = undefined;
    @Output() formSubmit: EventEmitter<object> = new EventEmitter<object>();
    @Input() customButtonName: string = '';
    form: FormGroup;
    validForm: boolean = true;
    submitButtonDisabled: boolean = false;
    readonly FormType = FormType;
    submitButtonSubscription: Subscription;

    constructor(private fb: FormBuilder) {}

    ngOnInit(): void {
        if (!this.formOptions || this.formOptions.length === 0) {
            throw new Error('From options are required!');
        }
        try {
            this.validateFormOptions(this.formOptions);
            this.form = this.createForm();
            this.resetSubmitButton();
        } catch (e) {
            this.validForm = false;
            throw new Error(e);
        }
    }

    createForm(): FormGroup {
        const formGroupConfig: any = {};
        try {
            this.formOptions.forEach((field: FormComponentOptions): void => {
                if (!field.slug) {
                    throw new Error('Slug is required for form field.');
                }
                const validators: ValidatorFn[] = this.mapValidators(
                    field.validator
                );
                formGroupConfig[field.slug] = [field?.value || null, [...validators]];
            });

            return this.fb.group(formGroupConfig);
        } catch (error) {
            console.error('Error creating form group:', error);
            return this.fb.group({});
        }
    }

    mapValidators(validators: FormValidator[] = []): ValidatorFn[] {
        return validators.map((validator: FormValidator) => validator.validate);
    }

    validateFormOptions(formOptions: FormComponentOptions[]): void {
        formOptions.forEach((option: FormComponentOptions): void => {
            this.validateRequiredProperties(option);
            this.validateNoWhitespace('Name', option.name);
            this.validateNoWhitespace('Slug', option.slug);
            this.validateSelectorOptions(option);
        });
    }

    onSubmit(): void {
        if (!this.form.valid) {
            return;
        }
        this.submitButtonDisabled = true;
        this.formSubmit.emit(this.form.value);

        this.resetSubmitButton();
    }

    selectRequired(validator: FormValidator[]): boolean {
        return !!validator.find((v) => v.slug === 'required');
    }

    validateRequiredProperties(option: FormComponentOptions): void {
        if (!option.name || !option.slug || !option.type) {
            throw new Error('Form option is missing required properties.');
        }
    }

    validateNoWhitespace(property: string, value: string): void {
        if (value.trim() !== value) {
            throw new Error(
                `"${property}" property cannot contain leading or trailing whitespace.`
            );
        }
    }

    validateSelectorOptions(option: FormComponentOptions): void {
        if (option.selectorOptions && option.type !== FormType.Selector) {
            throw new Error(
                'Selector options provided, but type is not "selector".'
            );
        }

        if (option.type === FormType.Selector && !option.selectorOptions) {
            throw new Error(
                'Type provided as "selector", but no selector options found.'
            );
        }
    }

    ngOnDestroy(): void {
        this.submitButtonSubscription.unsubscribe();
    }

    resetSubmitButton(): void {
        this.submitButtonDisabled = true;
        this.submitButtonSubscription = this.form.valueChanges.subscribe(() => {
            if (this.form.dirty) {
                this.submitButtonDisabled = false;
                this.submitButtonSubscription.unsubscribe();
            }
        });
    }
}
